import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import {
  ChangeDisplayNameAnonymousAction,
  DeleteUserProfileAction,
  EditNotificationSettingsAction,
  LoadDeleteUserProfileEligibilityAction,
  LoadUserProfileAction,
  ResetDeleteUserProfileFlowAction,
  ResetLoadingsAction,
  ResetUserProfileAction,
  SetTimezoneUserProfile,
  SetLanguageUserProfile,
  SetUserProfileAction,
  UpdateUserDefaultLocation,
  UpdateUserLiveLocationAction,
  UpdateUserLocationVisibilityAction,
  UpdateAppearInNearbyCountAction,
  UpdateShowsEnergyEstimatesAction,
  UpdateUserProfileAction,
} from './user-profile.actions';
import { UserLocationSelector } from './user-location.selector';
import { GeoPosition } from '../../locations/domains/geo-position';
import { GeoLocality } from '../../locations/domains/geo-locality';
import {
  DeleteUserEligibilityResponseDto,
  ProfileService,
  UpdateMyProfileDto,
  UserProfilePopulatedEntity,
} from '@smatchy/sdk';
import { catchError, EMPTY, finalize, tap, throwError } from 'rxjs';
import { JWTService } from '@smatchy/ionic-auth';
import { UserProfileSelector } from './user-profile.selector';
import { LoadActiveSportsAction } from '../../sports/state/sports.actions';
import { DisconnectUserAction } from '../../app/state/app.actions';
import {
  persistProfileHomeLocationSnapshot,
  seedProfileHomeLocationSnapshotIfMissing,
} from '../utils/profile-home-location.storage';

export type DeleteUserProfileErrorCode = 'delete_user_blocked' | 'delete_user_warning_required' | 'delete_user_failed';

export interface UserProfileStateModel {
  loading: boolean;
  updatingProfile: boolean;
  deletingProfile: boolean;
  deleteEligibilityLoading: boolean;
  deleteEligibilityLoaded: boolean;
  deleteEligibility?: DeleteUserEligibilityResponseDto;
  deleteProfileError?: DeleteUserProfileErrorCode;
  /** Absent until the profile is loaded, which is what the defaults below set. */
  profile?: UserProfilePopulatedEntity;
}

const defaults: UserProfileStateModel = {
  loading: false,
  updatingProfile: false,
  deletingProfile: false,
  deleteEligibilityLoading: false,
  deleteEligibilityLoaded: false,
  deleteEligibility: undefined,
  deleteProfileError: undefined,
  profile: undefined,
};

@State<UserProfileStateModel>({
  name: 'userProfile',
  defaults,
})
@Injectable()
export class UserProfileState {
  constructor(
    private readonly _store: Store,
    private readonly _profileService: ProfileService,
    private readonly _jwt: JWTService,
  ) {}

  @Action(LoadUserProfileAction)
  loadUserProfileAction({ patchState, dispatch }: StateContext<UserProfileStateModel>, _action: LoadUserProfileAction) {
    // CAPACITOR-T: during onboarding / auth-first several profile loads can
    // fire while no session is held. With no token the call can only answer
    // 401, and that failure escaped uncaught (a burst, five in one second). We
    // have nothing to load without a session, so skip the call. A token that IS
    // held but rejected (an expired or deleted account) still goes through; its
    // 401 is a real one and is handled below, in the catch, as the session
    // being over.
    if (!this._jwt.getAccessToken()) {
      patchState({ loading: false });
      return EMPTY;
    }

    patchState({
      loading: true,
    });

    // Return Observable so NGXS subscribes; with async the return would be Promise<Observable> and store would not update
    return this._profileService.getMe().pipe(
      tap((res) => {
        patchState({
          profile: res.value?.profile,
        });
        seedProfileHomeLocationSnapshotIfMissing(res.value?.profile);
        dispatch(new LoadActiveSportsAction());
      }),
      catchError((error) => {
        // A token was held (checked above) and the API still rejected it: the
        // session is over — expired, revoked, or an account removed on another
        // device. There is one right answer, signing out, and it is the one the
        // sibling profile actions already take on a 401 (getDeleteEligibility,
        // deleteProfile). Before this, a 401 here travelled uncaught to whoever
        // dispatched the reload — the app-resume CGV and notification checks in
        // home.component, and the profile sub-pages, do not catch it — and
        // escaped to the window handler as « 401 on /api/v1/profile »
        // (CAPACITOR-4, ten members). Handle it here, once, and stop it there.
        if (this.isUnauthorized(error)) {
          dispatch(new DisconnectUserAction());
          return EMPTY;
        }
        // Any other failure is a real one the caller may still want to act on;
        // it travels untouched, exactly as before.
        return throwError(() => error);
      }),
      /**
       * Whatever happens, the screen stops waiting.
       *
       * CAPACITOR-T: a 401 on this call during onboarding left `loading` true
       * for ever. The person saw a screen that never moved and a button they
       * could press as often as they liked. The 401 itself is the other half
       * of that ticket; this half is that a failure here must not leave the
       * application stuck, whatever the failure was.
       *
       * A 401 is handled just above — the session is over, so we sign out. Any
       * other failure still travels: what a failed profile load means then is
       * the caller's business, not this action's.
       */
      finalize(() => patchState({ loading: false })),
    );
  }

  @Action(SetUserProfileAction)
  async setUserProfileAction({ patchState }: StateContext<UserProfileStateModel>, { payload }: SetUserProfileAction) {
    patchState({
      profile: payload,
    });
  }

  @Action(UpdateUserProfileAction)
  async updateUserProfileAction(ctx: StateContext<UserProfileStateModel>, { payload }: UpdateUserProfileAction) {
    const locality = this._store.selectSnapshot(UserLocationSelector.locality);
    const position = this._store.selectSnapshot(UserLocationSelector.position);
    const currentProfile = ctx.getState().profile;
    ctx.patchState({
      updatingProfile: true,
      // The optimistic patch happens only when there IS a profile to patch.
      // Before one is loaded there is no edit screen to update, and spreading
      // an absent profile here wrote an object missing every field the model
      // requires -- invisible while this was untyped.
      ...(currentProfile
        ? {
            profile: {
              ...currentProfile,
              firstName: payload.firstName,
              lastName: payload.lastName,
              avatarUrl: payload?.avatarUrl || currentProfile.avatarUrl,
              sex: payload?.sex ?? currentProfile.sex,
              dateOfBirth: payload?.dateOfBirth ?? currentProfile.dateOfBirth,
              isMyLastNameAnonymised: !payload.isMyLastNameFullyDisplayed,
              description: payload.description ?? currentProfile.description,
              /*
               * `undefined` leaves it alone, `null` clears it: the two are not
               * the same answer here, so `??` would quietly refuse the clear.
               * Spread and asserted because the generated client's profile has
               * no weight on it yet; the reader in the domain is the way in.
               */
              ...(payload.weightKg === undefined ? {} : { weightKg: payload.weightKg ?? undefined }),
              // Same undefined/null handling as the weight, and spread for the
              // same reason: the generated client's profile has no Instagram
              // yet, so the domain reader is the way in.
              ...(payload.instagram === undefined ? {} : { instagram: payload.instagram ?? undefined }),
            } as UserProfilePopulatedEntity,
          }
        : {}),
    });

    /*
     * The generated client has no weight yet. Regenerating it rewrites hundreds
     * of files and would bury this feature in unrelated drift, the same reason
     * blocked-attendees-warning reads its field by hand on the way in. This is
     * the way out: one extra property, named and typed, not a cast.
     */
    const updateDto: UpdateMyProfileDto & { weightKg?: number | null; instagram?: string | null } = {
      avatarUrl: payload?.avatarUrl || undefined,
      firstName: payload.firstName,
      lastName: payload.lastName,
      city: locality ? locality.city : payload.city,
      sex: payload?.sex ?? undefined,
      isDisplayNameAnonym: !payload.isMyLastNameFullyDisplayed,
      // Sent even when empty, because clearing a bio is something somebody
      // does on purpose with the field in front of them. That is why it is
      // here and not in the guarded block below, like the date of birth.
      description: payload.description ?? '',
    };

    // Left out of the payload when there is none, rather than sent as
    // undefined: the profile update replaces what it receives, and somebody
    // who never gave a date of birth must not have one written over the field
    // every time they change their first name.
    if (payload.dateOfBirth) {
      updateDto.dateOfBirth = payload.dateOfBirth;
    }

    // Sent when the member said something about it, including when what they
    // said is "remove it". Left out entirely when they did not touch the field,
    // so editing a first name never writes over a weight.
    if (payload.weightKg !== undefined) {
      updateDto.weightKg = payload.weightKg;
    }

    // Sent only when the member touched the field, including to clear it, so
    // editing anything else never writes over a stored handle. Same rule as the
    // weight just above.
    if (payload.instagram !== undefined) {
      updateDto.instagram = payload.instagram;
    }

    if (locality && position) {
      // The whole location subdocument is replaced on save, so anything left
      // out here is erased. This form used to send three fields, which meant
      // editing a first name silently dropped the place id and the
      // administrative hierarchy that the location screen had just written,
      // and with them the profile's chance of being found by a search on a
      // department, a region or a country. The other two paths that write a
      // location (UpdateUserDefaultLocation and the live location) have always
      // sent both; this one is the odd one out.
      updateDto.location = {
        lat: position.lat,
        lon: position.lng,
        name: locality.city ?? '',
        placeId: locality.placeId,
        hierarchy: locality.hierarchy,
      };
      updateDto.country = locality.country;
    }

    this._profileService
      .updateProfile({
        updateMyProfileDto: updateDto,
      })
      .subscribe((_) => {
        ctx.patchState({
          updatingProfile: false,
        });

        ctx.dispatch(new LoadUserProfileAction());
      });
  }

  @Action(ChangeDisplayNameAnonymousAction)
  async changeDisplayNameAnonymousAction(
    { patchState }: StateContext<UserProfileStateModel>,
    { isAnonymous }: ChangeDisplayNameAnonymousAction,
  ) {
    patchState({
      updatingProfile: true,
    });
    this._profileService
      .anonymisedMyLastName({
        anonymisedMyLastNameDto: {
          isAnonymised: isAnonymous,
        },
      })
      .subscribe((_) => {
        patchState({
          updatingProfile: false,
        });
      });
  }

  @Action(UpdateUserDefaultLocation)
  async updateUserDefaultLocation(
    { patchState, dispatch }: StateContext<UserProfileStateModel>,
    _action: UpdateUserDefaultLocation,
  ) {
    // No annotation: the selector answers nothing until a position is known,
    // and the guard below already returns for that. Naming a type here only
    // stopped the guard from narrowing.
    const userLocality = this._store.selectSnapshot(UserLocationSelector.locality);
    const geoPosition = this._store.selectSnapshot(UserLocationSelector.position);
    if (!userLocality || !geoPosition) {
      return;
    }
    patchState({
      updatingProfile: true,
    });
    const { placeName, locationName } = this.resolveLocationLabels(userLocality);

    this._profileService
      .updateProfile({
        updateMyProfileDto: {
          city: placeName || userLocality.city,
          country: userLocality.country,
          location: {
            lat: geoPosition.lat,
            lon: geoPosition.lng,
            name: locationName,
            placeId: userLocality.placeId,
            hierarchy: userLocality.hierarchy,
          },
          locationCity: placeName || userLocality.city,
        },
      })
      .subscribe((_) => {
        persistProfileHomeLocationSnapshot({
          locality: userLocality,
          position: geoPosition,
        });
        patchState({
          updatingProfile: false,
        });

        dispatch(new LoadUserProfileAction());
      });
  }

  @Action(UpdateUserLiveLocationAction)
  async updateUserLiveLocation(
    { patchState, dispatch }: StateContext<UserProfileStateModel>,
    _action: UpdateUserLiveLocationAction,
  ) {
    // No annotation: the selector answers nothing until a position is known,
    // and the guard below already returns for that. Naming a type here only
    // stopped the guard from narrowing.
    const userLocality = this._store.selectSnapshot(UserLocationSelector.locality);
    const geoPosition = this._store.selectSnapshot(UserLocationSelector.position);
    if (!userLocality || !geoPosition) {
      return;
    }
    patchState({
      updatingProfile: true,
    });
    const { placeName, locationName } = this.resolveLocationLabels(userLocality);

    return this._profileService
      .updateProfile({
        updateMyProfileDto: {
          liveLocation: {
            lat: geoPosition.lat,
            lon: geoPosition.lng,
            name: locationName,
            placeId: userLocality.placeId,
            hierarchy: userLocality.hierarchy,
          },
          locationCity: placeName || userLocality.city,
          locationEnabled: true,
        },
      })
      .pipe(
        tap(() => {
          patchState({
            updatingProfile: false,
          });
          dispatch(new LoadUserProfileAction());
        }),
      );
  }

  private resolveLocationLabels(userLocality: GeoLocality): {
    placeName: string;
    locationName: string;
  } {
    const placeName =
      userLocality.adminLevel === 'region'
        ? userLocality.hierarchy?.region?.name || userLocality.region || userLocality.city
        : userLocality.adminLevel === 'country'
        ? userLocality.hierarchy?.country?.name || userLocality.country || userLocality.city
        : userLocality.hierarchy?.city?.name || userLocality.city;
    const locationName = userLocality.suggestionDetail || userLocality.formattedAddress || placeName || '';

    // `?? ''` on the place name: every branch above falls back on a city that
    // the locality declares optional, so the whole chain can answer nothing.
    // An empty name is what a place with none has to show.
    return { placeName: placeName ?? '', locationName };
  }

  @Action(UpdateUserLocationVisibilityAction)
  updateUserLocationVisibility(
    { patchState, dispatch }: StateContext<UserProfileStateModel>,
    { enabled }: UpdateUserLocationVisibilityAction,
  ) {
    patchState({
      updatingProfile: true,
    });

    return this._profileService
      .updateProfile({
        updateMyProfileDto: {
          locationEnabled: enabled,
        },
      })
      .pipe(
        tap(() => {
          patchState({
            updatingProfile: false,
          });
          dispatch(new LoadUserProfileAction());
        }),
      );
  }

  @Action(UpdateAppearInNearbyCountAction)
  updateAppearInNearbyCount(
    { patchState, dispatch }: StateContext<UserProfileStateModel>,
    { enabled }: UpdateAppearInNearbyCountAction,
  ) {
    patchState({
      updatingProfile: true,
    });

    return this._profileService
      .updateProfile({
        updateMyProfileDto: {
          appearInNearbyCount: enabled,
        },
      })
      .pipe(
        tap(() => {
          patchState({
            updatingProfile: false,
          });
          dispatch(new LoadUserProfileAction());
        }),
      );
  }

  /**
   * The member's own switch for the energy figures before a session.
   *
   * Sent as its own field rather than through the edit form, like the nearby
   * count above: the setting lives on its own screen and saving it must not
   * replay a whole profile the member is not looking at.
   *
   * The generated client has no such field yet, so it is named on the way out
   * the same way it is named on the way in. See the-advice-before-a-session.
   */
  @Action(UpdateShowsEnergyEstimatesAction)
  updateShowsEnergyEstimates(
    { patchState, dispatch }: StateContext<UserProfileStateModel>,
    { enabled }: UpdateShowsEnergyEstimatesAction,
  ) {
    patchState({
      updatingProfile: true,
    });

    return this._profileService
      .updateProfile({
        updateMyProfileDto: {
          showsEnergyEstimates: enabled,
        } as UpdateMyProfileDto & { showsEnergyEstimates: boolean },
      })
      .pipe(
        tap(() => {
          patchState({
            updatingProfile: false,
          });
          dispatch(new LoadUserProfileAction());
        }),
      );
  }

  @Action(LoadDeleteUserProfileEligibilityAction)
  loadDeleteUserProfileEligibilityAction({ patchState, dispatch }: StateContext<UserProfileStateModel>) {
    patchState({
      deleteEligibilityLoading: true,
      deleteEligibilityLoaded: false,
      deleteEligibility: undefined,
      deleteProfileError: undefined,
    });

    return this._profileService.getDeleteEligibility().pipe(
      tap((res) => {
        patchState({
          deleteEligibilityLoading: false,
          deleteEligibilityLoaded: true,
          deleteEligibility: this.getDeleteEligibilityFromResponse(res),
        });
      }),
      catchError((error) => {
        patchState({
          deleteEligibilityLoading: false,
          deleteEligibilityLoaded: true,
          deleteProfileError: 'delete_user_failed',
        });
        if (this.isUnauthorized(error)) {
          dispatch(new DisconnectUserAction());
        }
        return EMPTY;
      }),
    );
  }

  @Action(DeleteUserProfileAction)
  deleteUserProfileAction(
    { patchState, dispatch, getState }: StateContext<UserProfileStateModel>,
    { acceptedCreditLoss }: DeleteUserProfileAction,
  ) {
    const eligibility = getState().deleteEligibility;
    if (!this.canDeleteProfile(eligibility, acceptedCreditLoss)) {
      patchState({
        deleteProfileError: eligibility?.warnings?.length
          ? 'delete_user_warning_required'
          : eligibility?.blockers?.length
          ? 'delete_user_blocked'
          : 'delete_user_failed',
      });
      return EMPTY;
    }

    patchState({
      deletingProfile: true,
      deleteProfileError: undefined,
    });

    return this._profileService.deleteProfile({ acceptedCreditLoss }).pipe(
      tap(() => {
        patchState({
          deletingProfile: false,
        });

        // The account has just been removed, including on Firebase. Nothing
        // authenticated can succeed from here, and asking anyway is what
        // CAPACITOR-D and CAPACITOR-R report.
        dispatch(new DisconnectUserAction(true));
      }),
      catchError((error) => {
        patchState({
          deletingProfile: false,
        });
        if (this.isUnauthorized(error)) {
          dispatch(new DisconnectUserAction());
          return EMPTY;
        }

        const deleteError = this.getDeleteProfileError(error);
        const eligibility = this.getDeleteEligibilityFromError(error);
        patchState({
          deleteProfileError: deleteError,
          deleteEligibility: eligibility,
        });
        return EMPTY;
      }),
    );
  }

  @Action(ResetDeleteUserProfileFlowAction)
  resetDeleteUserProfileFlowAction({ patchState }: StateContext<UserProfileStateModel>) {
    patchState({
      deletingProfile: false,
      deleteEligibilityLoading: false,
      deleteEligibilityLoaded: false,
      deleteEligibility: undefined,
      deleteProfileError: undefined,
    });
  }

  @Action(SetTimezoneUserProfile)
  async setTimezoneUserProfileAction(
    { patchState }: StateContext<UserProfileStateModel>,
    { payload }: SetTimezoneUserProfile,
  ) {
    const actualProfile = this._store.selectSnapshot(UserProfileSelector.profile);

    // Only when there is a profile to patch: the setting is sent either way,
    // and spreading an absent profile wrote one missing every required field.
    if (actualProfile) {
      patchState({
        profile: {
          ...actualProfile,
          timezone: payload,
        },
      });
    }

    this._profileService
      .updateProfile({
        updateMyProfileDto: {
          timezone: payload,
        },
      })
      .subscribe((_) => {});
  }

  @Action(SetLanguageUserProfile)
  async setLanguageUserProfileAction(
    { patchState }: StateContext<UserProfileStateModel>,
    { payload }: SetLanguageUserProfile,
  ) {
    const actualProfile = this._store.selectSnapshot(UserProfileSelector.profile);

    // Only when there is a profile to patch: the setting is sent either way,
    // and spreading an absent profile wrote one missing every required field.
    if (actualProfile) {
      patchState({
        profile: {
          ...actualProfile,
          language: payload,
        },
      });
    }

    this._profileService.updateLanguage({ updateProfileLanguageDto: { language: payload } }).subscribe((_) => {});
  }

  @Action(ResetLoadingsAction)
  async resetLoadings({ patchState }: StateContext<UserProfileStateModel>, _action: UpdateUserDefaultLocation) {
    patchState({
      deletingProfile: false,
      deleteEligibilityLoading: false,
      deleteEligibilityLoaded: false,
      updatingProfile: false,
      loading: false,
    });
  }

  @Action(ResetUserProfileAction)
  resetUserProfile({ setState }: StateContext<UserProfileStateModel>) {
    setState(defaults);
  }

  @Action(EditNotificationSettingsAction)
  async EditNotificationSettingsAction(
    { patchState }: StateContext<UserProfileStateModel>,
    { type, key, value }: EditNotificationSettingsAction,
  ) {
    this._profileService
      .editNotificationSetting({
        editNotificationParamsDto: {
          // @ts-expect-error the generated DTO types this field more narrowly than the API accepts
          type,
          key,
          value,
        },
      })
      .subscribe(() => {});

    const actualProfile = this._store.selectSnapshot(UserProfileSelector.profile);
    if (!actualProfile) {
      return;
    }

    /*
     * CAPACITOR-P, in production since 2.1.7. A profile that never had any
     * notification setting carries no `notifications` object at all, and
     * reading a branch of it threw: the screen went down on the first switch
     * somebody touched, twenty-two times over two days.
     *
     * The old shape also skipped the local update whenever the branch was
     * missing, so on the way to that crash the switch simply sprang back
     * while the request went out. Both are the same hole: the branch has to
     * be created, not required.
     */
    const notifications = actualProfile.notifications ?? {};
    const branch = notifications[type] ?? {};

    patchState({
      profile: {
        ...actualProfile,
        notifications: {
          ...notifications,
          [type]: {
            ...branch,
            [key]: value,
          },
        },
      },
    });
  }

  private isUnauthorized(error: unknown): boolean {
    return (error as { status?: number })?.status === 401;
  }

  private getDeleteProfileError(error: unknown): DeleteUserProfileErrorCode {
    const errorCode = (error as { error?: { error?: string } })?.error?.error;
    if (errorCode === 'delete_user_blocked' || errorCode === 'delete_user_warning_required') {
      return errorCode;
    }
    return 'delete_user_failed';
  }

  private getDeleteEligibilityFromError(error: unknown): DeleteUserEligibilityResponseDto | undefined {
    const body = (error as { error?: Partial<DeleteUserEligibilityResponseDto> })?.error;
    if (!body || !Array.isArray(body.blockers) || !Array.isArray(body.warnings)) {
      return undefined;
    }

    return {
      canDelete: body.canDelete ?? false,
      blocked: body.blocked ?? body.blockers.length > 0,
      blockers: body.blockers,
      warnings: body.warnings,
    };
  }

  private getDeleteEligibilityFromResponse(response: unknown): DeleteUserEligibilityResponseDto | undefined {
    const wrappedValue = (response as { value?: DeleteUserEligibilityResponseDto })?.value;
    if (this.isDeleteEligibilityResponse(wrappedValue)) {
      return wrappedValue;
    }
    if (this.isDeleteEligibilityResponse(response)) {
      return response;
    }
    return undefined;
  }

  private isDeleteEligibilityResponse(value: unknown): value is DeleteUserEligibilityResponseDto {
    const eligibility = value as Partial<DeleteUserEligibilityResponseDto> | undefined;
    return !!eligibility && Array.isArray(eligibility.blockers) && Array.isArray(eligibility.warnings);
  }

  // The only warnings a person can go past are about their credit: the refund
  // part going back to their card, said for information, and the bonus they
  // lose, accepted for at least the amount they were shown.
  private canDeleteProfile(
    eligibility: DeleteUserEligibilityResponseDto | undefined,
    acceptedCreditLoss?: number,
  ): boolean {
    if (!eligibility || eligibility.blocked) return false;
    if (eligibility.canDelete) return true;
    return (
      eligibility.warnings.length > 0 &&
      eligibility.warnings.every(
        (warning) =>
          warning.code === 'wallet_refunded_to_card' ||
          (warning.code === 'wallet_credit_forfeited' && (acceptedCreditLoss ?? 0) >= Number(warning.amount ?? 0)),
      )
    );
  }
}
